import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { companyHref } from "@/lib/entityRoutes";
import { apiRequest } from "@/lib/queryClient";

export interface SearchResponse {
  contacts: {
    id: string;
    name: string;
    email?: string | null;
    title?: string | null;
  }[];
  companies: { id: string; name: string; category: string }[];
}
export interface SearchResult {
  id: string;
  name: string;
  type: "company" | "contact" | "buyer" | "coi";
  subtitle?: string;
  href: string;
}
export function searchOptions(term: string) {
  return {
    queryKey: ["global-search", "raw", term, 10] as const,
    queryFn: async (): Promise<SearchResponse> => {
      const response = await apiRequest(
        "GET",
        `/api/search?q=${encodeURIComponent(term)}&limit=10`,
      );
      return response.json();
    },
    staleTime: 30_000,
    enabled: term.length >= 2,
  };
}
export function searchItems(data?: SearchResponse): SearchResult[] {
  if (!data) return [];
  return [
    ...(data.contacts ?? []).map(
      (c): SearchResult => ({
        id: c.id,
        name: c.name,
        type: "contact",
        subtitle: c.email || c.title || undefined,
        href: `/contacts/${c.id}`,
      }),
    ),
    ...(data.companies ?? []).map(
      (c): SearchResult => ({
        id: c.id,
        name: c.name,
        type:
          c.category === "Buyer Network"
            ? "buyer"
            : c.category === "COI"
              ? "coi"
              : "company",
        subtitle: c.category,
        href: companyHref(c.id, c.category),
      }),
    ),
  ];
}
export function useGlobalSearch(query: string, enabled = true) {
  const [term, setTerm] = useState(query.trim());
  useEffect(() => {
    const timer = setTimeout(() => setTerm(query.trim()), 250);
    return () => clearTimeout(timer);
  }, [query]);
  const result = useQuery({
    ...searchOptions(term),
    enabled: enabled && term.length >= 2,
  });
  return {
    ...result,
    results: searchItems(result.data),
    term,
    isSearching: query.trim() !== term || result.isLoading,
  };
}
